// Regras puras do modal de criação rápida de proposta (PropostaRapidaDialog).
//
// Ficam fora do componente porque são a parte arriscada da feature: precedência de
// campos na troca de modelo e montagem do payload. Sem JSX, testável isolado.
import { isTextoBloco, getBlockMeta } from './editor/blockTypes';
import { adicionarTextoAoCanvas } from './editor/canvas/canvasUtils';

// Quill emite '<p><br></p>' para conteúdo vazio. Sem esse guard, campos em branco
// salvam uma linha fantasma no documento e o check de dirty dispara à toa.
export const htmlVazio = (html) =>
  !html || !String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

// Espelha DEFAULT_PROPOSTA.blocos do PropostaEditor: é o documento que o backend
// monta quando não há modelo nenhum cadastrado. Deriva 3 campos de texto
// (Apresentação, Forma de pagamento, Observações).
export const BLOCOS_PADRAO_SEM_MODELO = [
  { tipo: 'data', titulo: '', conteudo_html: '', config: { cidade: '' }, ordem: 0 },
  { tipo: 'dados_cliente', titulo: '', conteudo_html: '', config: {}, ordem: 1 },
  { tipo: 'texto', titulo: 'Apresentação', conteudo_html: '', config: {}, ordem: 2 },
  { tipo: 'itens', titulo: '', conteudo_html: '', config: {}, ordem: 3 },
  { tipo: 'forma_pagamento', titulo: 'Forma de pagamento', conteudo_html: '', config: {}, ordem: 4 },
  { tipo: 'validade', titulo: '', conteudo_html: '', config: {}, ordem: 5 },
  { tipo: 'observacoes', titulo: 'Observações', conteudo_html: '', config: {}, ordem: 6 }
];

export const ITEM_VAZIO = { descricao: '', quantidade: 1, preco_unitario: 0, desconto_percent: 0 };

/**
 * Ordena os blocos e devolve um campo de formulário por bloco de TEXTO.
 * Blocos dinâmicos (data, validade, dados_cliente, itens) não entram: eles
 * renderizam a partir de campos estruturados, não têm nada pra digitar.
 *
 * `fonte` é um template (modo criar) ou a própria proposta (modo editar).
 */
export function derivarCamposTexto(fonte) {
  const blocos = blocosOrdenados(fonte);
  return blocos.reduce((acc, bloco, indice) => {
    if (!isTextoBloco(bloco.tipo)) return acc;
    acc.push({
      // tipo + ordinal (não o índice do array) pra chave sobreviver à troca de
      // modelo: dois modelos com um forma_pagamento casam na mesma chave.
      key: `${bloco.tipo}#${acc.filter(c => c.tipo === bloco.tipo).length}`,
      indice, // endereço de escrita no array JÁ ORDENADO
      tipo: bloco.tipo,
      label: bloco.titulo || getBlockMeta(bloco.tipo).label,
      placeholder: getBlockMeta(bloco.tipo).descricao,
      valorInicial: bloco.conteudo_html || ''
    });
    return acc;
  }, []);
}

// O `indice` dos campos aponta pro array ordenado, então quem guarda os blocos
// base no estado tem que guardar esta versão.
export function blocosOrdenados(fonte) {
  return [...(fonte?.blocos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
}

export function textosIniciais(campos) {
  return campos.reduce((acc, campo) => {
    acc[campo.key] = { html: campo.valorInicial, dirty: false };
    return acc;
  }, {});
}

/**
 * Escreve os textos do formulário de volta nos blocos, preservando titulo,
 * config, ordem e os blocos dinâmicos — só troca conteudo_html.
 */
export function blocosComTextos(blocosBase, textos, campos) {
  const porIndice = new Map(campos.map(c => [c.indice, textos[c.key]?.html]));
  return blocosBase.map((bloco, i) => {
    const html = porIndice.get(i);
    if (html === undefined) return bloco;
    return { ...bloco, conteudo_html: htmlVazio(html) ? '' : html };
  });
}

/**
 * Troca de modelo: campo sujo (digitado pelo vendedor OU preenchido pela IA)
 * sobrevive; campo intocado adota a prosa do novo modelo.
 *
 * Rastrear `dirty` em vez de "não-vazio" é o que evita carregar o boilerplate do
 * modelo A pra frente como se o vendedor tivesse escrito, mascarando pra sempre
 * a prosa do modelo B.
 */
export function migrarTextos(textosAntigos, camposNovos) {
  return camposNovos.reduce((acc, campo) => {
    const anterior = textosAntigos[campo.key];
    acc[campo.key] = anterior?.dirty
      ? anterior
      : { html: campo.valorInicial, dirty: false };
    return acc;
  }, {});
}

/**
 * Aplica a resposta do agente de IA sobre o estado do formulário.
 *
 * Só a parte estruturada: titulo, itens e os dois textos semânticos. `visual` e
 * `documento` são ignorados de propósito — reconstroem o canvas, e neste fluxo o
 * vendedor não vê o documento.
 *
 * Tudo que a IA preenche vira dirty, pra sobreviver a uma troca de modelo depois.
 */
export function aplicarRespostaIa(resposta, estado, { modoCanvas }) {
  const { titulo, itens, observacoes, forma_pagamento: formaPagamento } = resposta || {};
  const proximo = {
    titulo: estado.titulo,
    itens: estado.itens,
    textos: { ...estado.textos },
    campos: estado.campos,
    blocosBase: estado.blocosBase,
    textoExtra: estado.textoExtra || ''
  };

  if (titulo) proximo.titulo = { valor: titulo, dirty: true };
  if (itens?.length) {
    proximo.itens = {
      valor: itens.map(item => ({
        descricao: item.descricao || '',
        quantidade: Number(item.quantidade) || 1,
        preco_unitario: Number(item.preco_unitario) || 0,
        desconto_percent: Number(item.desconto_percent) || 0
      })),
      dirty: true
    };
  }

  const secoes = [
    { tipo: 'observacoes', titulo: 'Observações', html: observacoes, regex: /observa/i },
    { tipo: 'forma_pagamento', titulo: 'Forma de pagamento', html: formaPagamento, regex: /pagamento/i }
  ].filter(s => s.html);

  // Duas seções nunca escrevem no mesmo campo: um modelo com um único bloco
  // "Observações e forma de pagamento" casaria nos dois rótulos e a segunda
  // sobrescreveria a primeira sem aviso.
  const usados = new Set();

  secoes.forEach((secao) => {
    if (modoCanvas) {
      // Canvas: o texto do documento vem do modelo, então a única entrada é o campo
      // "Texto adicional". Escrevemos nele (e não direto no canvas) para o vendedor
      // revisar antes de salvar, e para tudo virar UM bloco anexado — vários blocos
      // sobrepõem o rodapé do documento. Idempotente: rodar a IA de novo não duplica.
      if (!proximo.textoExtra.includes(secao.html)) {
        proximo.textoExtra += `<p><strong>${secao.titulo}</strong></p>${secao.html}`;
      }
      return;
    }

    // Modo blocos, em ordem de preferência: campo do mesmo tipo → qualquer campo de
    // texto cujo rótulo casa (inclui os tipos legados apresentacao/condicoes/escopo,
    // que é o que o Modelo Padrão usa) → cria um bloco novo no fim do documento.
    const disponivel = c => !usados.has(c.key);
    const alvo = proximo.campos.find(c => c.tipo === secao.tipo && disponivel(c))
      || proximo.campos.find(c => secao.regex.test(c.label) && disponivel(c));

    if (alvo) {
      usados.add(alvo.key);
      proximo.textos[alvo.key] = { html: secao.html, dirty: true };
      return;
    }

    // `ordem` acima da maior existente (e não blocosBase.length) para o bloco novo
    // realmente cair no fim: modelos podem ter ordem esparsa (0, 5, 10).
    const maiorOrdem = proximo.blocosBase.reduce((max, b) => Math.max(max, b.ordem || 0), -1);
    proximo.blocosBase = [...proximo.blocosBase, {
      tipo: secao.tipo,
      titulo: secao.titulo,
      conteudo_html: '',
      config: {},
      ordem: maiorOrdem + 1
    }];
    proximo.campos = derivarCamposTexto({ blocos: proximo.blocosBase });
    proximo.textos = migrarTextos(proximo.textos, proximo.campos);
    // Só chegamos aqui porque não havia nenhum campo deste tipo, então o recém
    // criado é o ordinal 0 — chave estável, independente da posição na lista.
    const criadoKey = `${secao.tipo}#0`;
    proximo.textos[criadoKey] = { html: secao.html, dirty: true };
    usados.add(criadoKey);
  });

  return proximo;
}

/**
 * Monta o corpo do POST/PUT.
 *
 * Deliberadamente NÃO envia:
 *  - `layout`  → o controller herda template.layout; enviar apagaria logo, tema_cor,
 *                dados da empresa e rodapé do modelo.
 *  - `canvas`  → herdado do modelo, exceto quando há texto extra a anexar.
 *  - `blocos` em modo canvas → o renderer canvas ignora o array blocos, e enviá-lo
 *                faria o controller resolver variáveis em peso morto.
 *  - numero, subtotal, valor_total, itens[].total, status, publicId, auditoria →
 *                calculados no servidor.
 *  - `assinatura` → omitir é o que faz valer o assinatura_default do PropostasConfig.
 *  - `automacao`  → vem do config_automacao do modelo.
 *  - `vendedor_id`→ default é o usuário do token, o próprio vendedor.
 *  - `validade_dias` quando intocado → deixa valer o default do modelo e depois o
 *                do cliente.
 */
/**
 * A tabela de itens e o valor total só aparecem no documento se existir um bloco
 * `itens` — nem o Modelo Padrão semeado nem um modelo feito à mão garantem isso.
 * Sem esta rede, o vendedor preenche os itens, vê o total no rodapé do modal e o
 * cliente recebe uma proposta sem preço nenhum.
 */
function garantirBlocoItens(blocos) {
  if (blocos.some(b => b.tipo === 'itens' || b.tipo === 'tabela_precos')) return blocos;
  const maiorOrdem = blocos.reduce((max, b) => Math.max(max, b.ordem || 0), -1);
  return [...blocos, { tipo: 'itens', titulo: '', conteudo_html: '', config: {}, ordem: maiorOrdem + 1 }];
}

export function montarPayload(estado, { chat, template, proposta, modo }) {
  const itens = (estado.itens.valor || [])
    .filter(item => (item.descricao || '').trim() || Number(item.preco_unitario) > 0)
    .map(item => ({
      descricao: (item.descricao || '').trim(),
      quantidade: Number(item.quantidade) || 0,
      preco_unitario: Number(item.preco_unitario) || 0,
      desconto_percent: Number(item.desconto_percent) || 0
    }));

  const base = {
    titulo: estado.titulo.valor.trim(),
    chat_id: chat?._id,
    lead_id: estado.lead_id || undefined,
    contato: {
      nome: estado.contato.nome || '',
      email: estado.contato.email || '',
      telefone: estado.contato.telefone || ''
    },
    itens,
    desconto_global_percent: Number(estado.desconto_global_percent) || 0,
    validade_dias: estado.validade_dias.dirty
      ? Number(estado.validade_dias.valor) || undefined
      : undefined,
    // Só na criação: manda o backend resolver as {{variáveis}} dos blocos. Na
    // edição elas já foram resolvidas e re-resolver não faria sentido.
    template_id: modo === 'criar' ? (template?._id || undefined) : undefined,
    mensagens_ia: estado.mensagens_ia?.length ? estado.mensagens_ia : undefined
  };

  const fonte = template || proposta;

  if (fonte?.modo_design === 'canvas') {
    const payload = { ...base, modo_design: 'canvas' };
    // Um único bloco anexado: adicionarTextoAoCanvas quebra página em PAGE_H - 40,
    // mas o rodapé dos modelos fica em PAGE_H - 60 — vários blocos o sobrepõem.
    if (!htmlVazio(estado.textoExtra)) {
      payload.canvas = adicionarTextoAoCanvas(fonte.canvas, { titulo: '', html: estado.textoExtra });
    }
    return payload;
  }

  const blocos = blocosComTextos(estado.blocosBase, estado.textos, estado.campos);
  return {
    ...base,
    modo_design: 'blocos',
    blocos: itens.length ? garantirBlocoItens(blocos) : blocos
  };
}
